'use strict';
/* globals VXConfig */

import FluxEventEmitter from '../FluxEventEmitter';
import Routes from '../../utils/Routes';
import assign from 'object-assign';
import { doFetch } from '../../utils/CommonUtils';
import Flux from '../Flux';
import Constants from '../Constants';

const CHANGE_EVENTS = {
	INCENTIVES: 'incentives',
	REFILL_WINDOW_HIDDEN: 'refillWindowHidden',
	CLOSE: 'close',
};

const _incentives = {};
let _vxpayConfig = {};
let _trackingData = [];

const ConversionStore = assign({}, FluxEventEmitter.prototype, {
	EVENTS: CHANGE_EVENTS,

	getExitIntentIncentive() {
		const incentives = this.getIncentivesByRegion(Constants.ConversionIncentiveRegions.EXIT_INTENT);
		let incentive = null;
		if (incentives.length > 0) {
			incentive = this.prepareIncentive(incentives.shift());
		}

		return incentive;
	},

	getIncentiveByRegion(region) {
		const incentives = this.getIncentivesByRegion(region);
		return incentives.length > 0 ? this.prepareIncentive(incentives[0]) : null;
	},

	getIncentivesByRegion(region) {
		let incentives = [];
		if (typeof _incentives[region] !== 'undefined' && _incentives[region].length > 0) {
			// sort incentives by priority
			_incentives[region].sort(this.compareIncentives);
			incentives = _incentives[region];
		}
		return incentives;
	},

	prepareIncentive(incentive) {
		if (typeof incentive.countdown !== 'undefined' && incentive.countdown > 0) {
			const date = new Date();
			if (typeof incentive.countdownStartTime !== 'undefined') {
				incentive.countdown = Math.max(0, incentive.countdown - Math.round((date - incentive.countdownStartTime) / 1000));
			}
			incentive.countdownStartTime = date;
		}
		return incentive;
	},

	compareIncentives(incentiveA, incentiveB) {
		const prioA = typeof incentiveA.priority !== 'undefined' ? incentiveA.priority : 0;
		const prioB = typeof incentiveB.priority !== 'undefined' ? incentiveB.priority : 0;

		if (prioA > prioB) {
			return -1;
		}

		return prioA < prioB ? 1 : 0;
	},

	clearRegion(region) {
		_incentives[region] = [];
		this.emit(CHANGE_EVENTS.INCENTIVES, Constants.ConversionEventTypes.CLEAR, region);
	},

	getVXPayConfig() {
		return _vxpayConfig;
	},

	setVXPayConfig(vxpayConfig = {}) {
		_vxpayConfig = vxpayConfig;
	},

	getTrackingData() {
		return _trackingData;
	},

	setTrackingData(trackingData = []) {
		_trackingData = trackingData;
	},

	registerTabHiddenListener() {
		let isSent = false; // Tracks if the 'comeback' event was already sent
		let oldTitle = document.title; // Store the initial title
		let animationIntervalId = null; // To store the interval ID for clearing later
		const animationSpeedMs = 100; // Adjust for desired speed (milliseconds)
		const separator = ' | '; // Separator between prefix and title, and for wrapping

		// Determine if we should animate the title
		const shouldAnimateTitle = Boolean(VXConfig.titleTabHiddenPrefix);

		// Event Tracking Setup
		const eventTracker = Flux.Tracker.getEventTracker();
		const event = eventTracker.getNewEvent();
		event.context = 'global';
		event.source = 'www';
		// Format data as requested: [sid_value]:[withtext|notext]
		const sidValue = VXConfig.sidSuffix || '';
		const textIndicator = shouldAnimateTitle ? 'animation' : 'static';
		event.data = `${sidValue}:${textIndicator}`;

		// Add listener for visibility changes
		Flux.Browser.addEventListener(Flux.Browser.EVENTS.visibilityChange, () => {
			if (document.hidden) {
				// Tab becomes hidden

				// Store the current title if needed
				if (!animationIntervalId) {
					oldTitle = document.title;
				}

				// Only perform title animation if we have a prefix configured
				if (shouldAnimateTitle) {
					// Prepare the text to animate
					const textToAnimate = VXConfig.titleTabHiddenPrefix + separator + oldTitle + separator;

					// Handle Unicode characters correctly
					const titleChars = Array.from(textToAnimate);

					// Define the animation function
					const animateTitle = () => {
						const firstChar = titleChars.shift();
						titleChars.push(firstChar);
						document.title = titleChars.join('');
					};

					// Start the animation interval if not already running
					if (!animationIntervalId) {
						document.title = titleChars.join('');
						animationIntervalId = setInterval(animateTitle, animationSpeedMs);
					}
				}

				// Track 'leave' event (only once per hide cycle until comeback)
				if (!isSent) {
					event.type = 'leave';
					eventTracker.trackEvent(event);
				}
			} else {
				// Tab becomes visible

				// Clear the animation interval if it's running
				if (animationIntervalId) {
					clearInterval(animationIntervalId);
					animationIntervalId = null;
				}

				// Restore the original title if it was changed
				if (shouldAnimateTitle) {
					document.title = oldTitle;
				}

				// Track 'comeback' event (only once)
				if (!isSent) {
					event.type = 'comeback';
					eventTracker.trackEvent(event);
					isSent = true; // Prevent sending 'leave'/'comeback' again until next hide
				}
			}
		});

		// Initial setup for edge case: If the page loads while hidden
		oldTitle = document.title; // Always capture title on load
	},

	// Direct method calls instead of actions
	closeIncentive: function (incentive, data) {
		this.deleteIncentive(incentive);
		this.redeemIncentive(incentive, data).finally(() => {
			this.emit(CHANGE_EVENTS.CLOSE);
		});
	},

	deleteIncentive(incentive) {
		if (incentive && incentive.region) {
			const incentives = _incentives[incentive.region];
			if (incentives) {
				let i = incentives.length - 1;
				while (i >= 0) {
					if (incentives[i] === incentive) {
						incentives.splice(i, 1);
						break;
					} else {
						i--;
					}
				}
			}
		}
	},

	redeemIncentive(incentive, data = {}) {
		const payload = { result: data, incentive };
		return doFetch(Routes.getRoute(Routes.Names.CONVERSION_CLOSE_INCENTIVE, {}), payload, Constants.HttpMethods.POST, false).then((result) => {
			if (result?.data.success) {
				this.loadPageLoadIncentives(VXConfig.routeName, VXConfig.routeArgs);
			}
		});
	},

	deleteIncentivesByAttribute(incentives, attribute, value) {
		let i = incentives.length - 1;
		while (i >= 0) {
			if (incentives[i][attribute] === value) {
				incentives.splice(i, 1);
			}
			i--;
		}
	},

	deleteIncentivesByEvent(event) {
		for (const r in _incentives) {
			if (Object.hasOwn(_incentives, r)) {
				this.deleteIncentivesByAttribute(_incentives[r], 'event', event);
			}
		}
	},

	addIncentiveToRegion(incentive) {
		const region = incentive.region;
		if (region) {
			if (typeof _incentives[region] === 'undefined') {
				_incentives[region] = [];
			}

			// delete all incentives of this type that are already in the region
			this.deleteIncentivesByAttribute(_incentives[region], 'type', incentive.type);

			// add incentive to region
			_incentives[region].push(incentive);
		}
	},

	addIncentives(incentives) {
		for (let i = 0; i < incentives.length; i++) {
			this.addIncentiveToRegion(incentives[i]);
		}
	},

	loadPageLoadIncentives: function (routeName, routeArgs) {
		let payload = null;
		if (routeName && routeArgs) {
			payload = {
				routeName: routeName,
				routeArgs: JSON.stringify(routeArgs),
			};
		}

		doFetch(Routes.getRoute(Routes.Names.CONVERSION_GET_PAGE_LOAD_INCENTIVES), payload, Constants.HttpMethods.POST, true).then((result) => {
			this.deleteIncentivesByEvent(Constants.ConversionEventTypes.PAGE_LOAD);

			if (result.success) {
				this.addIncentives(result.data.incentives);
				this.setVXPayConfig(result.data.vxpayConfig);
				this.setTrackingData(result.data.trackingData);
				this.emit(CHANGE_EVENTS.INCENTIVES, Constants.ConversionEventTypes.PAGE_LOAD);
			} else {
				this.emit(CHANGE_EVENTS.INCENTIVES, Constants.ConversionEventTypes.PAGE_LOAD);
			}
		});
	},

	loadVideoPreviewEndIncentives: function (albumId) {
		doFetch(
			Routes.getRoute(Routes.Names.CONVERSION_GET_VIDEO_PREVIEW_END_INCENTIVES, { albumId: albumId }),
			{},
			Constants.HttpMethods.GET,
			true,
		).then((result) => {
			this.deleteIncentivesByEvent(Constants.ConversionEventTypes.VIDEO_PREVIEW_ENDED);

			if (result.success) {
				this.addIncentives(result.data.incentives);
				this.emit(CHANGE_EVENTS.INCENTIVES, Constants.ConversionEventTypes.VIDEO_PREVIEW_ENDED, albumId);
			} else {
				this.emit(CHANGE_EVENTS.INCENTIVES, Constants.ConversionEventTypes.VIDEO_PREVIEW_ENDED, albumId);
			}
		});
	},

	setGuestAlreadySignedUp: function (alreadySignedUp) {
		if (VXConfig.routeName === 'Lindemann' && !Flux.Guest.isLoggedIn()) {
			return;
		}

		if (!alreadySignedUp) {
			this.loadPageLoadIncentives(VXConfig.routeName, VXConfig.routeArgs);
		} else {
			doFetch(
				Routes.getRoute(Routes.Names.CONVERSION_SET_GUEST_INFO),
				{
					type: Constants.ConversionGuestInfoTypes.GUEST_ALREADY_SIGNED_UP,
					value: true,
				},
				Constants.HttpMethods.POST,
				true,
			).then(() => {
				this.loadPageLoadIncentives(VXConfig.routeName, VXConfig.routeArgs);
			});
		}
	},

	setConversionWindowHidden: function (incentiveType, interval, incentiveRegion) {
		doFetch(
			Routes.getRoute(Routes.Names.CONVERSION_SET_REFILL_WINDOW_HIDDEN),
			{
				incentiveType: incentiveType,
				interval: interval,
			},
			Constants.HttpMethods.POST,
			true,
		).then((result) => {
			if (_incentives[incentiveRegion]) {
				this.deleteIncentivesByAttribute(_incentives[incentiveRegion], 'type', incentiveType);
			}
			this.emit(CHANGE_EVENTS.REFILL_WINDOW_HIDDEN, result.data.success, incentiveType);
		});
	},
});

export default ConversionStore;
